import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useConfirmDialog } from '../../../contexts/ConfirmDialogContext';
import { lessonsService, LessonId } from '../services/lessonsService';

interface LessonGuideShellProps {
  /** 對應的 Lesson id，用於跳過時寫入狀態 */
  lessonId: LessonId;
  /** 無障礙標籤，例如 "Lesson 1: Clear your head" */
  ariaLabel: string;
  /** Header 左上角副標題（灰色小字），例如 "Clear your head · Step 1 / 2" */
  subtitle: string;
  /** Header 主標題（白色粗體），例如 "See how Orlo clears your head" */
  title: string;
  onClose: () => void;
  /** 所在頁面有 FAB（行動版底部浮動按鈕），卡片行動版自動往上避讓 */
  avoidFAB?: boolean;
  children: React.ReactNode;
}

const BASE_CLASS =
  'fixed left-4 right-4 sm:left-auto sm:right-4 sm:w-72 rounded-2xl bg-white border border-gray-200 shadow-lg z-floating overflow-hidden';

/**
 * Lesson Guide 共用的浮動卡片外殼。
 * 固定在右下角，透過 portal 掛載於 body。
 *
 * 點擊 X 時先彈出 confirm dialog，讓使用者選擇「之後再看」或「跳過這個 Lesson」，
 * 避免誤觸關閉鍵被當成跳過（skip 為不可逆的永久終結狀態）。
 */
export function LessonGuideShell({
  lessonId,
  ariaLabel,
  subtitle,
  title,
  onClose,
  avoidFAB = false,
  children,
}: LessonGuideShellProps) {
  const { showConfirm } = useConfirmDialog();

  const handleCloseClick = async () => {
    const shouldSkip = await showConfirm({
      type: 'warning',
      title: 'Pause this lesson?',
      message: '"Later" keeps it on your list as a reminder. "Skip" removes it for good.',
      confirmText: 'Skip',
      cancelText: 'Later',
      cancelIsPrimary: true,
    });
    if (shouldSkip) {
      lessonsService.markSkipped(lessonId);
    }
    onClose();
  };

  // 行動版：有 FAB 時往上推（bottom-40），桌面版始終 bottom-24（sm: 覆蓋）
  const positionClass = avoidFAB
    ? 'bottom-40 sm:bottom-24'
    : 'bottom-24';

  const shell = (
    <div
      className={`${positionClass} ${BASE_CLASS}`}
      role="dialog"
      aria-label={ariaLabel}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-700">
        <div>
          <p className="text-xs text-slate-300">{subtitle}</p>
          <p className="text-sm font-semibold text-white">{title}</p>
        </div>
        <button
          onClick={handleCloseClick}
          aria-label="Close lesson guide"
          className="p-1 rounded-md text-slate-300 hover:text-white hover:bg-slate-600 transition-colors shrink-0 ml-2"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-3">
        {children}
      </div>
    </div>
  );

  return createPortal(shell, document.body);
}
